import Spin from "components/spin/spin";
import { useAppDispatch } from "hooks/redux";
import Input from "components/form/input/index";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import Portal from "elements/Portal";
import { IWebsiteBuilderIconAssetIdentity } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { WEBSITE_BUILDER_PORTAL } from "../../../../WebsiteBuilderEditor";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { websiteBuilderAddIconAssets } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { useWebsiteBuilderIconPicker } from "./useWebsiteBuilderIconPicker";

interface IWebsiteBuilderSelectIconProps {
  assetIdentity: IWebsiteBuilderIconAssetIdentity;
  isOpen: boolean;
  closeModal: () => void;
  handleSelectIcon: (assetIdentity: IWebsiteBuilderIconAssetIdentity) => void;
}

export function WebsiteBuilderSelectIcon(props: IWebsiteBuilderSelectIconProps) {
  const { assetIdentity, isOpen, closeModal, handleSelectIcon } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  
  const { iconPickerState, lastIconRef, setSelectedVariantCode, setAssetSearchValue } = useWebsiteBuilderIconPicker(assetIdentity, isOpen, ".select-icon-options");

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeModal();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    document.addEventListener("keydown", handleKeydown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeydown, { capture: true });
  }, [isOpen, handleKeydown]);

  return (
    <Portal isMounted={isOpen} parentId={WEBSITE_BUILDER_PORTAL}>
      <Sheet
        title={t("Select icon")}
        size="small"
        open={isOpen}
        onCancel={closeModal}
        closable
      >
        <div className="website-builder-select-icon">
          {iconPickerState.variantsLoading ? (
            <div className="select-icon-loading-spin-container">
              <Spin className="select-icon-loading-spin" />
            </div>
          ) : (
            <>
              <div className="select-icon-controls">
                <Select
                  label={t("Variant")}
                  defaultValue={iconPickerState.selectedVariantCode}
                  onChange={setSelectedVariantCode}
                  disabled={iconPickerState.variantsLoading}
                  usePortal
                  portalId={WEBSITE_BUILDER_PORTAL}
                >
                  {iconPickerState.variants.map(variant => (
                    <Select.Option key={variant.code} value={variant.code}>
                      {variant.title}
                    </Select.Option>
                  ))}
                </Select>
                <Input
                  labelHidden
                  value={iconPickerState.assetSearchValue}
                  placeholder={t("Search icons...")}
                  onChange={e => setAssetSearchValue(e.target.value)}
                  onSearchClear={() => setAssetSearchValue("")}
                />
              </div>
              {iconPickerState.assetsLoading ? (
                <div className="select-icon-loading-spin-container">
                  <Spin className="select-icon-loading-spin" />
                </div>
              ) : (
                <div className="select-icon-options">
                  {iconPickerState.filteredAssets.slice(0, iconPickerState.renderCount).map((asset, index) => (
                    <OverlayTrigger
                      key={asset.asset_code}
                      placement="right"
                      popperConfig={{ strategy: "fixed" }}
                      transition={false}
                      delay={{
                        show: 500,
                        hide: 0,
                      }}
                      overlay={
                        <Tooltip id={`website-builder-select-icon-asset-${asset.asset_code}`}>
                          {asset.title}
                        </Tooltip>
                      }
                    >
                      <button
                        ref={index === Math.min(iconPickerState.filteredAssets.length, iconPickerState.renderCount) - 1 ? lastIconRef : null}
                        type="button"
                        onClick={() => {
                          dispatch(websiteBuilderAddIconAssets({ iconAssets: [asset] }));

                          handleSelectIcon({
                            providerCode: asset.provider_code,
                            versionCode: asset.version_code,
                            variantCode: asset.variant_code,
                            assetCode: asset.asset_code,
                          });
                        }}
                        dangerouslySetInnerHTML={{ __html: asset.svg }}
                      />
                    </OverlayTrigger>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Sheet>
    </Portal>
  );
}
